import { defineStore } from 'pinia';
import {
	type Project,
	type PurchaseOrder,
	type Quote,
} from '@/generated/graphql';
import {
	type LinkableFinanceDocument,
	type LinkableFinanceDocumentLine,
	type LinkingPayload,
	type PurchaseDocument,
	type RetrospectiveLink,
	type PurchaseOrderLineFromDetailsQuery,
	type SettledState,
} from '../types';
import {
	getChangedTargetLineIds,
	buildSettlementStates,
	requiresNotification,
} from './helpers';

export interface RetrospectiveLinkingStoreType {
	activeDocumentId: Quote['id'] | PurchaseOrder['id'] | undefined;
	activeDocumentProjectIds: Project['id'][];
	links: Record<LinkableFinanceDocumentLine['id'], RetrospectiveLink>;
	removedPersistedLinks: Record<
		LinkableFinanceDocumentLine['id'],
		RetrospectiveLink
	>;
	settledStates: Record<
		PurchaseOrderLineFromDetailsQuery['id'],
		SettledState
	>;
	inProgressDragging: LinkingPayload | null;
	currentPurchaseDocumentType: PurchaseDocument | null;
	currentPurchaseDocumentId: LinkableFinanceDocument['id'] | null;
	documentsNeedingRefetch: Set<Quote['id'] | PurchaseOrder['id']>;
	forceRefetch: boolean;
	initialPurchaseOrderTargetLines: Record<
		PurchaseOrder['id'],
		PurchaseOrderLineFromDetailsQuery[]
	>;
	initialPurchaseOrderStatuses: Record<
		PurchaseOrder['id'],
		string | null | undefined
	>;
}

export const useRetrospectiveLinkingStore = defineStore(
	'retrospectiveLinking',
	{
		state: (): RetrospectiveLinkingStoreType => ({
			activeDocumentId: undefined,
			activeDocumentProjectIds: [],
			links: {},
			removedPersistedLinks: {},
			settledStates: {},
			inProgressDragging: null,
			currentPurchaseDocumentType: null,
			currentPurchaseDocumentId: null,
			documentsNeedingRefetch: new Set(),
			forceRefetch: false,
			initialPurchaseOrderTargetLines: {},
			initialPurchaseOrderStatuses: {},
		}),
		getters: {
			isDocumentSelected(state) {
				return !!state.activeDocumentId;
			},
			hasAnyChanges(state) {
				return (
					Object.keys(state.links).length > 0 ||
					Object.keys(state.removedPersistedLinks).length > 0 ||
					Object.keys(state.settledStates).length > 0
				);
			},
			linkedDocumentCount(state) {
				return (
					Object.keys(state.links).length -
					Object.keys(state.removedPersistedLinks).length
				);
			},
			/**
			 * Returns Purchase Order IDs that require status change notifications.
			 *
			 * Notification is triggered when settlement changes occur:
			 * 1. All lines unsettled → at least 1 settled
			 * 2. All lines settled → at least 1 unsettled
			 * 3. Some lines settled → all settled
			 * 4. Lines changed but none settled (and PO not in partially_settled status)
			 */
			purchaseOrdersRequiringNotification(state) {
				const changedLineIds = getChangedTargetLineIds(state);
				const purchaseOrderIds = new Set<PurchaseOrder['id']>();

				Object.entries(state.initialPurchaseOrderTargetLines).forEach(
					([purchaseOrderId, lines]) => {
						const changedLines = lines.filter((line) =>
							changedLineIds.has(line.id)
						);

						if (changedLines.length === 0) {
							return;
						}

						const settlementStates = buildSettlementStates(
							changedLines,
							state.settledStates
						);

						if (
							requiresNotification(
								settlementStates,
								state.initialPurchaseOrderStatuses[
									purchaseOrderId
								]
							)
						) {
							purchaseOrderIds.add(purchaseOrderId);
						}
					}
				);

				return Array.from(purchaseOrderIds);
			},
		},
		actions: {
			addLink(input: RetrospectiveLink) {
				this.links = {
					...this.links,
					[input.lineId]: input,
				};
			},

			removeLink(input: RetrospectiveLink) {
				if (!this.links[input.lineId]) {
					this.removedPersistedLinks[input.lineId] = input;
				}

				const { [input.lineId]: _removedLink, ...newLinks } =
					this.links;

				this.links = newLinks;
			},

			setSettledState(
				lineId: PurchaseOrderLineFromDetailsQuery['id'],
				documentId: PurchaseOrder['id'],
				isSettled: boolean
			) {
				this.settledStates = {
					...this.settledStates,
					[lineId]: {
						isSettled,
						documentId,
					},
				};
			},

			clearSettledState(lineId: PurchaseOrderLineFromDetailsQuery['id']) {
				const { [lineId]: _removedState, ...newSettledStates } =
					this.settledStates;

				this.settledStates = newSettledStates;
			},

			activateDocument(documentId: undefined | string) {
				this.activeDocumentId = documentId;
			},

			deactivateDocument() {
				this.activeDocumentId = undefined;
				this.activeDocumentProjectIds = [];
			},

			startDragging(link: LinkingPayload) {
				this.inProgressDragging = link;
			},

			stopDragging() {
				this.inProgressDragging = null;
			},
		},
	}
);
